import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Film, ImageIcon, Inbox, Link2, Loader2, Lock, LogOut, Settings2, ShieldCheck, UploadCloud, Users, X, XCircle,
} from "lucide-react";
import api, { uploadFileChunked } from "@/lib/api";
import { CATEGORIES, fmtBytes, formatApiError } from "@/components/admin/shared";
import { MediaGrid } from "@/components/admin/MediaGrid";
import { SiteSettings } from "@/components/admin/SiteSettings";
import { ProofingRooms } from "@/components/admin/ProofingRooms";

const TABS = [
  { id: "media", label: "Media", icon: ImageIcon },
  { id: "rooms", label: "Proofing Rooms", icon: Users },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "settings", label: "Contact Details", icon: Settings2 },
];

let queueId = 0;

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("media");

  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Weddings", location: "", specs: "" });
  const [uploading, setUploading] = useState(false);
  const [embed, setEmbed] = useState({ title: "", video_url: "", thumbnail_url: "", category: "Films" });
  const [embedSaving, setEmbedSaving] = useState(false);
  const inputRef = useRef(null);

  const refresh = async () => {
    const [m, s, en] = await Promise.all([
      api.get("/media"),
      api.get("/admin/stats"),
      api.get("/admin/enquiries"),
    ]);
    setMedia(m.data);
    setStats(s.data);
    setEnquiries(en.data);
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) refresh().catch(() => {});
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("aaf_token", data.token);
      setUser(data.user);
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("aaf_token");
    setUser(null);
  };

  const pickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setQueue((q) => [
      ...q,
      ...files.map((f) => ({
        id: ++queueId,
        file: f,
        name: f.name,
        size: f.size,
        progress: 0,
        status: "queued",
      })),
    ]);
  };

  const removeQueued = (id) => setQueue((q) => q.filter((x) => x.id !== id));

  const uploadAll = async () => {
    const pending = queue.filter((x) => x.status === "queued" || x.status === "error");
    if (pending.length === 0) {
      toast.error("Drop one or more photos/videos first.");
      return;
    }
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const item of pending) {
      setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "uploading", progress: 0 } : x)));
      try {
        const title = queue.length === 1 && form.title.trim() ? form.title.trim() : item.name.replace(/\.[^.]+$/, "");
        await uploadFileChunked(
          item.file,
          { title, category: form.category, location: form.location, specs: form.specs },
          (p) => setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, progress: p } : x))),
        );
        ok += 1;
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "done", progress: 100 } : x)));
      } catch (err) {
        fail += 1;
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "error" } : x)));
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} published to the site.`);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed — hit Publish to retry.`);
    setQueue((q) => q.filter((x) => x.status !== "done"));
    if (ok) {
      setForm((p) => ({ ...p, title: "" }));
      refresh();
    }
  };

  const saveEmbed = async () => {
    if (!embed.video_url.trim()) {
      toast.error("Paste a YouTube, Vimeo or MP4 link first.");
      return;
    }
    setEmbedSaving(true);
    try {
      await api.post("/media/embed", embed);
      toast.success("Film added to the showreel.");
      setEmbed({ title: "", video_url: "", thumbnail_url: "", category: "Films" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEmbedSaving(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-amber" size={22} />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-screen" className="min-h-screen bg-ink flex items-center justify-center px-5 relative">
        <div className="grain-overlay" aria-hidden />
        <div
          aria-hidden
          className="absolute -top-10 left-1/2 -translate-x-1/2 w-[60vw] max-w-[600px] h-[70vh] animate-beam pointer-events-none"
          style={{
            clipPath: "polygon(46% 0, 54% 0, 88% 100%, 12% 100%)",
            background: "linear-gradient(180deg, rgba(247,244,238,0.15) 0%, transparent 90%)",
            filter: "blur(14px)",
          }}
        />
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="relative w-full max-w-sm border border-ivory/10 bg-coal/80 backdrop-blur-xl p-8"
        >
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.jpeg" alt="ANSH AGARWAL FILMS" className="w-16 h-16 object-cover rounded-sm ring-1 ring-ivory/20" />
            <p className="font-display text-sm tracking-[0.35em] text-ivory mt-5 uppercase">Studio Access</p>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mute mt-2">Owner sign-in only</p>
          </div>
          <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Email</label>
          <input
            data-testid="admin-login-input-email"
            type="email"
            className="field mt-1 mb-5"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
          <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Password</label>
          <input
            data-testid="admin-login-input-password"
            type="password"
            className="field mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="font-mono text-[11px] text-red-400 mt-4">{loginError}</p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loginLoading}
            className="w-full mt-8 font-mono text-[11px] tracking-[0.3em] uppercase bg-ivory text-ink py-4 hover:bg-amber disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loginLoading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
            {loginLoading ? "Unlocking…" : "Enter Studio"}
          </button>
          <Link to="/" data-testid="admin-back-to-site" className="block text-center font-mono text-[10px] tracking-[0.25em] uppercase text-mute hover:text-amber mt-6 transition-colors">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-ivory">
      <header className="border-b border-ivory/10 bg-coal/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="logo" className="w-9 h-9 object-cover rounded-sm ring-1 ring-ivory/20" />
            <span className="font-display text-xs tracking-[0.3em] uppercase">Studio Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-button" className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-ash hover:text-amber border border-line hover:border-amber/50 px-4 py-2.5 transition-all">
              <ArrowLeft size={12} /> View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-ash hover:text-red-400 border border-line hover:border-red-400/50 px-4 py-2.5 transition-all"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 space-y-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: ImageIcon, label: "Photographs", value: stats?.photos ?? "—" },
            { icon: Film, label: "Films", value: stats?.films ?? "—" },
            { icon: UploadCloud, label: "Storage Used", value: fmtBytes(stats?.bytes) },
            { icon: Inbox, label: "Enquiries", value: stats?.enquiries ?? "—" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="border border-line bg-coal p-5">
                <Icon size={16} className="text-amber" />
                <p className="font-display text-2xl sm:text-3xl text-ivory mt-4">{s.value}</p>
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-mute mt-1.5">{s.label}</p>
              </div>
            );
          })}
        </div>

        <nav data-testid="admin-tabs" className="flex flex-wrap gap-2 border-b border-line pb-px">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-3 border-b-2 -mb-px transition-all ${
                  active ? "border-amber text-ivory" : "border-transparent text-mute hover:text-ash"
                }`}
              >
                <Icon size={12} className={active ? "text-amber" : ""} /> {t.label}
                {t.id === "enquiries" && enquiries.length > 0 && (
                  <span className="font-mono text-[9px] text-ink bg-amber px-1.5 py-0.5 tracking-normal">{enquiries.length}</span>
                )}
              </button>
            );
          })}
        </nav>

        {tab === "rooms" && <ProofingRooms />}
        {tab === "settings" && <SiteSettings />}

        {tab === "enquiries" && (
          <section className="border border-line bg-coal p-6 sm:p-8">
            <h2 className="font-display text-sm tracking-[0.3em] uppercase text-ivory flex items-center gap-2">
              <Inbox size={14} className="text-amber" /> Enquiries
              <span className="font-mono text-[10px] text-mute tracking-normal">({enquiries.length})</span>
            </h2>
            <div className="mt-5 space-y-3" data-testid="admin-enquiries-list">
              {enquiries.length === 0 ? (
                <p className="font-mono text-[11px] text-mute">No enquiries yet — they appear here the moment someone writes in.</p>
              ) : (
                enquiries.map((q) => (
                  <div key={q.id} className="border border-line/70 p-4">
                    <div className="flex justify-between gap-3 flex-wrap">
                      <p className="font-mono text-xs text-ivory">{q.name} — <span className="text-amber">{q.event_type}</span></p>
                      <p className="font-mono text-[10px] text-mute">{new Date(q.created_at).toLocaleDateString()}</p>
                    </div>
                    <p className="font-mono text-[10px] text-ash mt-1">{q.email} {q.shoot_date ? `· ${q.shoot_date}` : ""} {q.budget ? `· ${q.budget}` : ""}</p>
                    <p className="font-mono text-[11px] text-cream mt-2 leading-relaxed">{q.message}</p>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {tab === "media" && (<>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="border border-line bg-coal p-6 sm:p-8">
            <h2 className="font-display text-sm tracking-[0.3em] uppercase text-ivory">Upload Photos / Videos</h2>
            <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2">
              JPG · PNG · WEBP · MP4 · MOV · WEBM — drop many at once, large files supported
            </p>
            <p data-testid="admin-upload-protection-note" className="font-mono text-[10px] tracking-[0.15em] uppercase text-amber/80 mt-1.5 flex items-center gap-1.5">
              <ShieldCheck size={11} /> Originals stay locked in storage — visitors only ever receive protected web-size copies
            </p>
            <div
              data-testid="admin-upload-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFiles(e.dataTransfer.files);
              }}
              className={`mt-6 border border-dashed p-10 text-center cursor-pointer transition-all duration-300 ${
                dragging ? "border-amber bg-amber/5" : "border-line hover:border-ivory/40"
              }`}
            >
              <UploadCloud size={26} className="mx-auto text-amber" />
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-ash mt-4">
                Drop files here or click to browse
              </p>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-mute mt-2">
                Select a whole folder of frames — they queue up below
              </p>
              <input
                ref={inputRef}
                data-testid="admin-upload-file-input"
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  pickFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {queue.length > 0 && (
              <div className="mt-5 space-y-2.5" data-testid="admin-upload-queue">
                {queue.map((item, i) => (
                  <div key={item.id} data-testid={`admin-upload-queue-item-${i}`} className="border border-line/70 bg-ink p-3">
                    <div className="flex items-center gap-3">
                      {item.status === "done" ? (
                        <CheckCircle2 size={14} className="text-amber shrink-0" />
                      ) : item.status === "error" ? (
                        <XCircle size={14} className="text-red-400 shrink-0" />
                      ) : item.status === "uploading" ? (
                        <Loader2 size={14} className="text-amber animate-spin shrink-0" />
                      ) : (
                        <ImageIcon size={14} className="text-mute shrink-0" />
                      )}
                      <p className="font-mono text-[11px] text-ivory truncate flex-1">{item.name}</p>
                      <span className="font-mono text-[9px] text-mute shrink-0">{fmtBytes(item.size)}</span>
                      {item.status !== "uploading" && (
                        <button
                          data-testid={`admin-upload-queue-remove-${i}`}
                          aria-label={`Remove ${item.name}`}
                          onClick={() => removeQueued(item.id)}
                          className="text-mute hover:text-red-400 transition-colors shrink-0"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    {(item.status === "uploading" || item.status === "done") && (
                      <div className="h-0.5 bg-line mt-2.5 overflow-hidden">
                        <div className="h-full bg-amber transition-all duration-300" style={{ width: `${item.progress}%` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-6">
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Title (single file only)</label>
                <input data-testid="admin-upload-input-title" className="field mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Category</label>
                <select data-testid="admin-upload-select-category" className="field mt-1 bg-coal" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-coal">{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Location (optional)</label>
                <input data-testid="admin-upload-input-location" className="field mt-1" placeholder="Udaipur Palace" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Camera / Specs (optional)</label>
                <input data-testid="admin-upload-input-specs" className="field mt-1" placeholder="Sony A1 · 35mm f/1.2" value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} />
              </div>
            </div>
            <button
              data-testid="admin-upload-submit-button"
              onClick={uploadAll}
              disabled={uploading || queue.filter((x) => x.status !== "done").length === 0}
              className="mt-7 w-full font-mono text-[11px] tracking-[0.3em] uppercase bg-ivory text-ink py-4 hover:bg-amber disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {uploading
                ? "Publishing…"
                : queue.length > 1
                  ? `Publish ${queue.filter((x) => x.status !== "done").length} Files to Site`
                  : "Publish to Site"}
            </button>
          </section>

          <div className="space-y-6">
            <section className="border border-line bg-coal p-6 sm:p-8">
              <h2 className="font-display text-sm tracking-[0.3em] uppercase text-ivory flex items-center gap-2">
                <Link2 size={14} className="text-amber" /> Add Film by Link
              </h2>
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-mute mt-2">
                YouTube, Vimeo or direct MP4 URL — plays in the showreel
              </p>
              <div className="space-y-5 mt-6">
                <div>
                  <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Film Title</label>
                  <input data-testid="admin-embed-input-title" className="field mt-1" value={embed.title} onChange={(e) => setEmbed({ ...embed, title: e.target.value })} />
                </div>
                <div>
                  <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Video URL *</label>
                  <input data-testid="admin-embed-input-url" className="field mt-1" placeholder="https://youtube.com/watch?v=…" value={embed.video_url} onChange={(e) => setEmbed({ ...embed, video_url: e.target.value })} />
                </div>
                <div>
                  <label className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute">Thumbnail URL (optional)</label>
                  <input data-testid="admin-embed-input-thumb" className="field mt-1" value={embed.thumbnail_url} onChange={(e) => setEmbed({ ...embed, thumbnail_url: e.target.value })} />
                </div>
                <button
                  data-testid="admin-embed-submit-button"
                  onClick={saveEmbed}
                  disabled={embedSaving}
                  className="w-full font-mono text-[11px] tracking-[0.3em] uppercase border border-ivory/30 text-ivory py-3.5 hover:border-amber hover:text-amber disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
                >
                  {embedSaving ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
                  Add to Showreel
                </button>
              </div>
            </section>

          </div>
        </div>

        <MediaGrid media={media} onChange={() => refresh().catch(() => {})} />
        </>)}
      </main>
    </div>
  );
}
